/**
 * Landmark card data.
 *
 * Phase 1 ships at least one card per `LandmarkEffect` kind and the full A–L
 * spread for the Forest habitat (so the sample-scenario D fixture can use
 * `forest_L` and the type system covers every effect shape). Other habitats
 * carry a smaller spread; the full 60-card catalog lands with P2 fixtures.
 *
 * Per `docs/task-log/20260505-cascadia-online/02-rules-spec.md` §9.5.
 */

import type { LandmarkCardDef } from "../types/landmark";

export const LANDMARK_CARDS: readonly LandmarkCardDef[] = [
  // Forest A–L (full spread)
  {
    cardId: "forest_A",
    habitat: "forest",
    variantId: "A",
    setLabel: "landmarks",
    effect: {
      kind: "pointsPerAnimalOnHabitat",
      habitat: "forest",
      wildlife: "bear",
      pointsPerAnimal: 2,
    },
  },
  {
    cardId: "forest_B",
    habitat: "forest",
    variantId: "B",
    setLabel: "landmarks",
    effect: {
      kind: "pointsPerAnimalOnHabitat",
      habitat: "forest",
      wildlife: "elk",
      pointsPerAnimal: 2,
    },
  },
  {
    cardId: "forest_C",
    habitat: "forest",
    variantId: "C",
    setLabel: "landmarks",
    effect: {
      kind: "pointsPerAnimalOnHabitat",
      habitat: "forest",
      wildlife: "salmon",
      pointsPerAnimal: 2,
    },
  },
  {
    cardId: "forest_D",
    habitat: "forest",
    variantId: "D",
    setLabel: "landmarks",
    effect: {
      kind: "pointsPerAnimalOnHabitat",
      habitat: "forest",
      wildlife: "hawk",
      pointsPerAnimal: 2,
    },
  },
  {
    cardId: "forest_E",
    habitat: "forest",
    variantId: "E",
    setLabel: "landmarks",
    effect: {
      kind: "pointsPerAnimalOnHabitat",
      habitat: "forest",
      wildlife: "fox",
      pointsPerAnimal: 2,
    },
  },
  {
    cardId: "forest_F",
    habitat: "forest",
    variantId: "F",
    setLabel: "landmarks",
    effect: { kind: "keystoneCountsAsHabitat", habitat: "forest" },
  },
  {
    cardId: "forest_G",
    habitat: "forest",
    variantId: "G",
    setLabel: "landmarks",
    effect: { kind: "singleTileCorridorCounts", habitat: "forest" },
  },
  {
    cardId: "forest_H",
    habitat: "forest",
    variantId: "H",
    setLabel: "landmarks",
    effect: { kind: "minTwoTileCorridor", habitat: "forest" },
  },
  {
    cardId: "forest_I",
    habitat: "forest",
    variantId: "I",
    setLabel: "landmarks",
    effect: {
      kind: "addToCorridor",
      habitat: "forest",
      pointsPerTileInLargest: 1,
    },
  },
  {
    cardId: "forest_J",
    habitat: "forest",
    variantId: "J",
    setLabel: "landmarks",
    effect: { kind: "addToMajority", habitat: "forest", bonus: 1 },
  },
  {
    cardId: "forest_K",
    habitat: "forest",
    variantId: "K",
    setLabel: "landmarks",
    effect: {
      kind: "cappedBonus",
      habitat: "forest",
      perTile: 1,
      capPoints: 5,
      floor: 0,
    },
  },
  {
    cardId: "forest_L",
    habitat: "forest",
    variantId: "L",
    setLabel: "landmarks",
    effect: { kind: "flatBonus", points: 3 },
  },

  // Mountain — sample
  {
    cardId: "mountain_L",
    habitat: "mountain",
    variantId: "L",
    setLabel: "landmarks",
    effect: { kind: "flatBonus", points: 3 },
  },
  {
    cardId: "mountain_G",
    habitat: "mountain",
    variantId: "G",
    setLabel: "landmarks",
    effect: { kind: "singleTileCorridorCounts", habitat: "mountain" },
  },

  // Prairie — sample
  {
    cardId: "prairie_L",
    habitat: "prairie",
    variantId: "L",
    setLabel: "landmarks",
    effect: { kind: "flatBonus", points: 3 },
  },

  // Wetland — sample
  {
    cardId: "wetland_L",
    habitat: "wetland",
    variantId: "L",
    setLabel: "landmarks",
    effect: { kind: "flatBonus", points: 3 },
  },

  // River — sample
  {
    cardId: "river_L",
    habitat: "river",
    variantId: "L",
    setLabel: "landmarks",
    effect: { kind: "flatBonus", points: 3 },
  },

  // KS Promo — placeholder
  {
    cardId: "river_ks_meander",
    habitat: "river",
    variantId: "ks_meander",
    setLabel: "ks",
    effect: { kind: "ksCustom", promoId: "river_meander" },
  },
];

export const LANDMARK_CARDS_BY_ID: Readonly<Record<string, LandmarkCardDef>> =
  Object.freeze(
    Object.fromEntries(LANDMARK_CARDS.map((card) => [card.cardId, card])),
  );

export const LANDMARK_CARDS_BY_HABITAT: Readonly<
  Record<string, readonly LandmarkCardDef[]>
> = Object.freeze(
  LANDMARK_CARDS.reduce<Record<string, LandmarkCardDef[]>>((acc, card) => {
    const list = acc[card.habitat] ?? [];
    list.push(card);
    acc[card.habitat] = list;
    return acc;
  }, {}),
);
